import React from "react";
import Breadcrumb from "../../Components/Utils/Breadcrumb";
import { Link, Outlet, useLocation } from "react-router-dom";
import { GetDashboardRole } from "../../Components/Utils/roles";

// Icon components for Tabs
const OverallIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

const SalesIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const ProcurementIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
  </svg>
);

const StoresIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

const MaintenanceIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

function DashboardLayout() {
  const location = useLocation();
  const pathFile = location.pathname;

  const LinkItem = [
    {
      label: "Overall",
      link: "/dashboard",
      icon: <OverallIcon />,
      access: GetDashboardRole(""),
    },
    {
      label: "Sales",
      link: "/dashboard/sales",
      icon: <SalesIcon />,
      access: GetDashboardRole("sales"),
    },
    {
      label: "Procurement",
      link: "/dashboard/procurement",
      icon: <ProcurementIcon />,
      access: GetDashboardRole("procurement"),
    },
    {
      label: "Stores",
      link: "/dashboard/stores",
      icon: <StoresIcon />,
      access: GetDashboardRole("store"),
    },
    {
      label: "Maintenance",
      link: "/dashboard/maintenance",
      icon: <MaintenanceIcon />,
      access: GetDashboardRole(""),
    },
  ];

  return (
    <div className="w-full h-full px-2 sm:px-4 py-2 overflow-hidden bg-slate-50/50">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
        <Breadcrumb items={[{ name: "Dashboard" }]} />
      </div>

      {/* Tab Navigation Header */}
      <div className="w-full bg-white rounded-2xl p-1.5 border border-slate-200/80 shadow-[0_2px_10px_rgba(0,0,0,0.03)] mb-4">
        <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto scrollbar-none pb-0.5 sm:pb-0">
          {LinkItem &&
            LinkItem.map((item) => {
              if (!item.access) return null;
              const isActive =
                item.link === "/dashboard"
                  ? pathFile === "/dashboard" || pathFile === "/dashboard/"
                  : pathFile.startsWith(item.link);

              return (
                <Link
                  to={item.link}
                  key={item.link}
                  className={`flex items-center gap-2 px-3.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 whitespace-nowrap shrink-0 ${
                    isActive
                      ? "bg-[#048178] text-white shadow-md shadow-[#048178]/25"
                      : "text-slate-600 hover:text-[#048178] hover:bg-slate-100/80"
                  }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              );
            })}
        </div>
      </div>

      {/* Dashboard View Canvas */}
      <div className="w-full h-[calc(100vh-145px)] overflow-y-auto pr-0 sm:pr-1 pb-6">
        <Outlet />
      </div>
    </div>
  );
}

export default DashboardLayout;


// import React from "react";
// import Breadcrumb from "../../Components/Utils/Breadcrumb";
// import { Link, Outlet, useLocation } from "react-router-dom";
// import { GetRole } from "../../Components/Utils/roles";

// function DashboardLayout() {
//     const location = useLocation();
//     const pathFile = location.pathname;
    
  
//     const role = GetRole(); 
//     console.log("Restore",role);
    

//     let LinkItem = [
//       {
//         label: "Overall",
//         link: "/dashboard",
//         access: true, 
//       },
//       {
//         label: "Sales",
//         link: "/dashboard/sales",
//         access: (role === "super admin" || role === "admin") || role ==="sales", 
//       },
//       {
//         label: "Procurement",
//         link: "/dashboard/procurement",
//         access: (role === "super admin" || role === "admin") || role === "procurement", 
//       },
//       {
//         label: "Stores",
//         link: "/dashboard/stores",
//         access: (role === "super admin" || role === "admin") || role === "store", 
//       },
//       {
//         label: "Maintenance",
//         link: "/dashboard/maintenance",
//         access: (role === "super admin" || role === "admin") || role === "maintenance", 
//       },
//     ];

//     const accessibleLinks = LinkItem.filter((item) => item.access);
    
//     return (
//       <div className="w-full h-full px-4 py-2 overflow-hidden">
//         <Breadcrumb items={[]} />

//         <div className="w-[100%] h-[calc(100vh-90px)] mt-2">
//           <div className="w-full h-8 flex items-center gap-x-1">
//             {
//               accessibleLinks && accessibleLinks.map((item, i) => (
//                 <Link
//                   to={item.link}
//                   className={`mx-3 pb-1 text-xs 2xl:text-sm font-medium text-baseColor-primary ${item.link === pathFile ? "border-b-2 border-baseColor-primary" : "border-b-2 border-transparent"}`}
//                   key={i}
//                 >
//                   {item.label || ""}
//                 </Link>
//               ))
//             }
//           </div>
//           <div className="w-[100%] h-[calc(100vh-141px)] overflow-y-auto">
//             <Outlet />
//           </div>
//         </div>
//       </div>
//     );
// }

// export default DashboardLayout;